import { useTaskPlannedOccurrenceForDate } from '@griply/hooks';
import { useMemo } from 'react';
import { DateRange } from 'shared/types/date-range';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getTaskPlanning } from 'shared/utils/get-task-planning';

export type UseTaskDisplayDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateRange?: DateRange;
};

export const useTaskDisplayDate = (
  task: Task,
  referenceDate: Timestamp,
  { today, weekStartsOn, dateRange }: UseTaskDisplayDateOptions,
  enabled = true,
): Timestamp => {
  const plannedOccurrenceInRange = useMemo(() => {
    if (!enabled || !dateRange) {
      return undefined;
    }

    const planning = getTaskPlanning(task, {
      ...dateRange,
      today,
      weekStartsOn,
      uncompletedOnly: true,
      sinceLastEntryOnly: true,
      strictEndDate: true,
    });

    return planning
      .slice()
      .sort((a, b) => a.date.getTime() - b.date.getTime())[0];
  }, [enabled, dateRange, task, today, weekStartsOn]);

  const plannedOccurrenceForReference = useTaskPlannedOccurrenceForDate(
    task,
    referenceDate,
    { today, weekStartsOn },
    enabled,
  );

  return (
    plannedOccurrenceInRange?.date ??
    plannedOccurrenceForReference?.date ??
    referenceDate
  );
};
